import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScanLine, ArrowRight, Maximize, Search, QrCode } from 'lucide-react';

const TraceabilitySearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [batchId, setBatchId] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        const trimmedBatchId = batchId.trim();
        if (!trimmedBatchId) return;
        navigate(`/traceability/${encodeURIComponent(trimmedBatchId)}`);
    };

    const hasValue = batchId.trim().length > 0;

    /* ══════════════════════════════════════════════════════════
       MOBILE VIEW — Figma 578:5982 (empty) & 578:6023 (filled)
       ══════════════════════════════════════════════════════════ */
    if (isMobile) {
        return (
            <div className="trace-search-wrapper mobile">
                <div className="trace-search-card mobile">
                    {/* Logo */}
                    <div className="trace-search-logo-section">
                        <ScanLine size={28} color="#071422" />
                        <span>
                            Syn<span className="green">Trace</span>
                        </span>
                    </div>

                    {/* Title & Description */}
                    <div className="trace-search-intro mobile">
                        <h1 className="mobile">
                            Scan QR Code
                        </h1>
                        <p>
                            Quickly trace the complete immutable history of your product by scanning the QR code
                        </p>
                    </div>

                    {/* QR Scanner Area */}
                    <div className="trace-qr-scanner-section">
                        <div className="qr-container">
                            <div className="qr-box"></div>
                            <div className="qr-overlay">
                                <Maximize size={290} color="#02C170" strokeWidth={0.4} />
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="trace-search-divider">
                        <div className="line"></div>
                        <span>Or Enter the Batch Code</span>
                        <div className="line"></div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSearch}>
                        <div className="trace-search-input-group">
                            <div className="search-icon">
                                <Search size={20} color="#90A1B9" />
                            </div>
                            <input 
                                type="text" 
                                placeholder="Enter Batch ID (e.g. BAT0001...)"
                                value={batchId}
                                onChange={(e) => setBatchId(e.target.value)}
                                className={hasValue ? 'has-value' : ''}
                            />
                        </div>

                        <button 
                            type="submit" 
                            disabled={!hasValue}
                            className={`trace-search-submit-btn mobile`}
                        >
                            Verify Traceability
                            <ArrowRight size={18} />
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    /* ══════════════════════════════════════════════════════════
       DESKTOP VIEW — Figma 630:7121 (empty) & 630:7162 (filled)
       ══════════════════════════════════════════════════════════ */
    return (
        <div className="trace-search-wrapper desktop">
            {/* White Card */}
            <div className="trace-search-card desktop">
                {/* Top Section - Icon + Title */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', width: '330px' }}>
                    {/* Green QR Icon */}
                    <div className="trace-desktop-search-icon-box">
                        <QrCode size={40} color="#FFFFFF" strokeWidth={1.8} />
                    </div>

                    {/* Title & Description */}
                    <div className="trace-search-intro desktop">
                        <h1 className="desktop">
                            Trace Your Product
                        </h1>
                        <p>
                            Scan a QR code or enter a Batch ID to view the complete immutable history of your product.
                        </p>
                    </div>
                </div>

                {/* Form Container */}
                <div className="trace-search-form-container desktop-card">
                    <form onSubmit={handleSearch} style={{ width: '100%' }}>
                        <div className="trace-search-input-group">
                            <div className="search-icon">
                                <Search size={20} color="#90A1B9" />
                            </div>
                            <input 
                                type="text" 
                                placeholder="Enter Batch ID (e.g. BAT0001...)"
                                value={batchId}
                                onChange={(e) => setBatchId(e.target.value)}
                                className={hasValue ? 'has-value' : ''}
                            />
                        </div>

                        <button 
                            type="submit" 
                            disabled={!hasValue}
                            className="trace-search-submit-btn desktop"
                        >
                            Verify Traceability
                            <ArrowRight size={18} />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TraceabilitySearch;
